// drift-test:no-test-needed: additive counter+label changes only (added "skipped" outcome counter + DROP/CXLD labels in the layer header). Closed-set parity with models.KnownSubCallOutcomes enforced by orchestrator/internal/contract/trace_invariants T27. Render test for the counter strip is a follow-up (no existing layer-level render test in the codebase to extend).
import { useState } from "react";
import type { LayerTrace, SubCallTrace, TraceError } from "../../lib/traceApi";
import { SubCallRow } from "./SubCallRow";
import { BridgeStepRow } from "./BridgeStepRow";
import { PerItemGroupRow } from "./PerItemGroupRow";
import {
  useTraceFilter,
  matchesQuery,
  subCallSearchable,
  bridgeStepSearchable,
} from "./TraceFilterContext";

export function LayerRow({
  layer,
  errorsOnly,
  parentStageErrors,
}: {
  layer: LayerTrace;
  errorsOnly: boolean;
  // Forwarded from StageRow's attempt-filtered errors so ExpandPanel's
  // Validation tab can bridge between sub-call success and stage failure.
  parentStageErrors?: TraceError[];
}) {
  const [open, setOpen] = useState(true);

  const subCalls = layer.sub_calls ?? [];
  // Group sub-calls by sub_prompt_id to detect per-item maps.
  const groups = new Map<string, SubCallTrace[]>();
  for (const sc of subCalls) {
    const arr = groups.get(sc.sub_prompt_id) ?? [];
    arr.push(sc);
    groups.set(sc.sub_prompt_id, arr);
  }

  if (errorsOnly) {
    const hasError = subCalls.some((sc) => sc.outcome !== "success");
    const bridgeFailed = layer.bridge?.outcome === "abort";
    if (!hasError && !bridgeFailed) return null;
  }

  // Tree-wide filter: hide this layer unless something in its subtree
  // matches the query (or the layer name itself matches).
  const { query } = useTraceFilter();
  if (query !== "") {
    const layerMatches = matchesQuery(query, [layer.name]);
    const subMatch = subCalls.some((sc) =>
      matchesQuery(query, subCallSearchable(sc)),
    );
    const bridgeMatch = (layer.bridge?.steps ?? []).some((step) =>
      matchesQuery(query, bridgeStepSearchable(step)),
    );
    if (!layerMatches && !subMatch && !bridgeMatch) return null;
  }

  const bridgeAborted = layer.bridge?.outcome === "abort";
  const bridgeError = layer.bridge?.error ?? "";
  // Status breakdown for the header — operator sees "what this layer did"
  // without expanding (item 3 of the trace-view refactor).
  const okCount = subCalls.filter((sc) => sc.outcome === "success").length;
  const failCount = subCalls.filter((sc) => sc.outcome === "abort").length;
  const fallbackCount = subCalls.filter((sc) => sc.outcome === "fallback").length;
  // canceledCount = cascade-cancelled by an earlier sibling's failure (no work done)
  // droppedCount = SkipItem terminal failure (sub-call exhausted attempts, item omitted from output)
  const canceledCount = subCalls.filter((sc) => sc.outcome === "canceled").length;
  const droppedCount = subCalls.filter((sc) => sc.outcome === "skipped").length;
  const layerDurationMs = layer.completed_at
    ? new Date(layer.completed_at).getTime() - new Date(layer.started_at).getTime()
    : 0;
  const layerCost = layer.total_cost_usd ?? 0;
  return (
    <div className="border-l border-gray-700 pl-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 w-full text-left hover:bg-gray-800 px-2 py-1 text-sm"
      >
        <span className="w-6 text-gray-500">{open ? "▼" : "▶"}</span>
        <span className="font-mono">Layer {layer.name}</span>
        <span className="text-gray-400 text-xs ml-auto flex items-center gap-2">
          <span>
            {okCount > 0 && <span className="text-emerald-300">{okCount} OK</span>}
            {failCount > 0 && <span className="text-rose-300"> · {failCount} FAIL</span>}
            {fallbackCount > 0 && <span className="text-amber-300"> · {fallbackCount} FB</span>}
            {droppedCount > 0 && <span className="text-amber-300"> · {droppedCount} DROP</span>}
            {canceledCount > 0 && <span className="text-ink-500"> · {canceledCount} CXLD</span>}
            {okCount + failCount + fallbackCount + droppedCount + canceledCount === 0 && (
              <span>{subCalls.length} sub-calls</span>
            )}
          </span>
          {layer.bridge && (
            <span>· {(layer.bridge.steps ?? []).length} bridge</span>
          )}
          <span>· {(layerDurationMs / 1000).toFixed(1)}s</span>
          {layerCost > 0 && <span>· ${layerCost.toFixed(3)}</span>}
          {bridgeAborted && <span className="text-rose-400">· bridge aborted</span>}
        </span>
      </button>
      {open && (
        <div className="ml-4 space-y-1">
          {Array.from(groups.entries()).map(([spID, calls]) =>
            calls.length > 5 ? (
              <PerItemGroupRow
                key={spID}
                subPromptID={spID}
                subCalls={calls}
                parentStageErrors={parentStageErrors}
              />
            ) : (
              calls.map((sc, i) => (
                <SubCallRow
                  key={`${spID}-${i}`}
                  subCall={sc}
                  parentStageErrors={parentStageErrors}
                />
              ))
            ),
          )}
          {/* Bridge-container failure: shown when the bridge body errored
              BEFORE any individual step ran (e.g. a pre-step nil check or
              context-cancel). Without this, the abort was invisible at
              the layer level — only the per-step error field surfaced
              anything, and there might be no steps to render. */}
          {bridgeAborted && bridgeError !== "" && (
            <div className="border-l-2 border-rose-600 bg-rose-950/40 pl-2 py-1 text-xs font-mono">
              <span className="text-rose-400">[bridge_container_abort]</span>{" "}
              <span className="text-rose-100">{bridgeError}</span>
            </div>
          )}
          {layer.bridge &&
            (layer.bridge.steps ?? []).map((step, i) => (
              <BridgeStepRow
                key={i}
                step={step}
                parentStageErrors={parentStageErrors}
              />
            ))}
        </div>
      )}
    </div>
  );
}
